import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DateUtilities, reformatDate } from '@approot/utilities';
import {
  firstDateToUseFn,
  frequencyToUseFn
} from '@approot/utilities/schedule-utilities';
import { removeDateLag } from '../utilities/leg-attribute-utilities';
import { LegDateAttributeService } from './leg-date-attribute.service';
import { LegDateService } from './leg-date.service';
import { LegHelperService } from './leg-helper.service';
import { LegScheduleService } from './leg-schedule.service';

@Injectable({
  providedIn: 'root'
})
export class LegDateListenersService {
  constructor(
    private legHelperService: LegHelperService,
    private legDateAttributeService: LegDateAttributeService,
    private legDateService: LegDateService,
    private legScheduleService: LegScheduleService
  ) {}

  public dateListeners(): void {
    const form = this.legHelperService.form;
    this.expiryDateListener(form);
    this.windowDateListener(form);
    this.windowStartDateListener(form);
    this.windowEndDateListener(form);
    this.strikeFixingDateListener(form);
    this.startDateListener(form);
    this.swapStartDateListener(form);
    this.swapEndDateListener(form);
    this.endDateListener(form);
    this.swaptionExpiryDateListener(form);
    this.swaptionSettlementDateListener(form);
    this.firstExerciseDateListener(form);
    this.nearDateListener(form);
    this.farDateListener(form);
    this.settlementDateListener(form);
    this.firstDateToUseListener(form);
    this.depositDateListener(form);
    this.redemptionDateListener(form);
    this.tradeDateListener(form);
    this.mtcDateListener(form);
    this.premiumPaymentDateListener(form);
    this.valueAdjustAmrtStartDateListener(form);
    this.valueAdjustAmrtEndDateListener(form);
  }

  private expiryDateListener(form: FormGroup): void {
    const leg = this.legHelperService.leg;
    if (form.get('expiryDate')) {
      this.legHelperService.subSink.sink = form
        .get('expiryDate')
        ?.valueChanges.subscribe(async _ => {
          // if expiry date changes wipe settlement date and process expiry date
          removeDateLag('expiryDate', leg); // attempt to remove (1m) if its there
          form.get('treasuryRate')?.setValue(''); // remove the treasuryrate as its linked to dates
          await this.legDateAttributeService.handleProductExpiryChange();
        });
    }
  }

  private windowDateListener(form: FormGroup): void {
    this.legHelperService.subSink.sink = form
      .get('windowDate')
      ?.valueChanges.subscribe(_ => {
        this.legDateAttributeService.onWindowDateChange();
      });
  }

  private windowStartDateListener(form: FormGroup): void {
    this.legHelperService.subSink.sink = form
      .get('windowStartDate')
      ?.valueChanges.subscribe(_ => {
        this.legDateAttributeService.onWindowStartDateChange();
      });
  }

  private windowEndDateListener(form: FormGroup): void {
    this.legHelperService.subSink.sink = form
      .get('windowEndDate')
      ?.valueChanges.subscribe(_ => {
        this.legDateAttributeService.onWindowEndDateChange();
      });
  }

  private strikeFixingDateListener(form: FormGroup): void {
    this.legHelperService.subSink.sink = form
      .get('strikeFixingDate')
      ?.valueChanges.subscribe(_ => {
        this.legDateAttributeService.onStrikeFixingDate();
      });
  }

  private startDateListener(form: FormGroup): void {
    this.legHelperService.subSink.sink = form
      .get('startDate')
      ?.valueChanges.subscribe(date => {
        this.legDateAttributeService.onStartDateChange(date);
      });
  }

  private swapStartDateListener(form: FormGroup): void {
    this.legHelperService.subSink.sink = form
      .get('swapStartDate')
      ?.valueChanges.subscribe(_ => {
        this.legDateAttributeService.onSwapStartDateChange();
      });
  }

  private swapEndDateListener(form: FormGroup): void {
    this.legHelperService.subSink.sink = form
      .get('swapEndDate')
      ?.valueChanges.subscribe(_ => {
        this.legDateAttributeService.onSwapEndDateChange();
      });

    this.legHelperService.subSink.sink = form
      .get('xccySwapEndDate')
      ?.valueChanges.subscribe(val => {
        this.legDateService.xccySwapDateAdd(val);
      });

    this.legHelperService.subSink.sink = form
      .get('xccySwapStartDate')
      ?.valueChanges.subscribe(xccySwapStartDate => {
        if (form.get('xccySwapStartDate').valid) {
          this.legScheduleService.xccySwapGetSchedules({
            xccySwapStartDate: reformatDate(xccySwapStartDate, 'YYYY-MM-DD')
          });
        }
      });
  }

  private endDateListener(form: FormGroup): void {
    this.legHelperService.subSink.sink = form
      .get('endDate')
      ?.valueChanges.subscribe(_ => {
        DateUtilities.onDateChanged(form.get('endDate'));
      });
  }

  private swaptionExpiryDateListener(form: FormGroup): void {
    this.legHelperService.subSink.sink = form
      .get('swaptionExpiryDate')
      ?.valueChanges.subscribe(_ => {
        this.legDateAttributeService.onSwaptionExpiryDateChange();
      });
  }

  private swaptionSettlementDateListener(form: FormGroup) {
    this.legHelperService.subSink.sink = form
      .get('swaptionSettlementDate')
      ?.valueChanges.subscribe(_ => {
        this.legDateAttributeService.onSwaptionSettlementDateChange();
      });
  }

  private firstExerciseDateListener(form: FormGroup): void {
    this.legHelperService.subSink.sink = form
      .get('firstExerciseDate')
      ?.valueChanges.subscribe(_ => {
        this.legDateAttributeService.onFirstExerciseDateChange();
      });
  }

  private nearDateListener(form: FormGroup): void {
    this.legHelperService.subSink.sink = form
      .get('nearDate')
      ?.valueChanges.subscribe(_ => {
        DateUtilities.onDateChanged(form.get('nearDate'));
      });
  }

  private farDateListener(form: FormGroup) {
    this.legHelperService.subSink.sink = form
      .get('farDate')
      ?.valueChanges.subscribe(_ => {
        DateUtilities.onDateChanged(form.get('farDate'));
      });
  }

  private settlementDateListener(form: FormGroup): void {
    this.legHelperService.subSink.sink = form
      .get('settlementDate')
      ?.valueChanges.subscribe(_ => {
        this.legDateAttributeService.onSettlementDateChange();
      });
  }

  private firstDateToUseListener(form: FormGroup): void {
    const firstDateToUse = firstDateToUseFn(form);
    const frequencyToUse = frequencyToUseFn(form);

    if (form.get(firstDateToUse)) {
      if (form.get('cross')) {
        form.get(firstDateToUse).disable();
      }
      this.legHelperService.subSink.sink = form
        .get(firstDateToUse)
        ?.valueChanges.subscribe(val => {
          this.legDateService.onFirstDateChange(
            val,
            firstDateToUse,
            frequencyToUse
          );
        });
    }
  }

  private depositDateListener(form: FormGroup): void {
    this.legHelperService.subSink.sink = form
      .get('depositDate')
      ?.valueChanges.subscribe(val => {
        this.legDateAttributeService.onDepositDateChange(val);
      });
  }

  private redemptionDateListener(form: FormGroup): void {
    this.legHelperService.subSink.sink = form
      .get('redemptionDate')
      ?.valueChanges.subscribe(val => {
        this.legDateService.onRedemptionDateChange(val);
      });
  }

  private tradeDateListener(form: FormGroup): void {
    this.legHelperService.subSink.sink = form
      .get('tradeDate')
      ?.valueChanges.subscribe(() => {
        DateUtilities.onDateChanged(form.get('tradeDate'));
      });
  }

  private mtcDateListener(form: FormGroup): void {
    this.legHelperService.subSink.sink = form
      .get('mtcDate')
      ?.valueChanges.subscribe(() => {
        DateUtilities.onDateChanged(form.get('mtcDate')); // If form control value is not dd month yyyy
      });
  }

  private premiumPaymentDateListener(form: FormGroup): void {
    this.legHelperService.subSink.sink = form
      .get('premiumPaymentDate')
      ?.valueChanges.subscribe(date => {
        this.legDateAttributeService.onPremiumPaymentDateChange(date);
      });
  }

  private valueAdjustAmrtStartDateListener(form: FormGroup): void {
    this.legHelperService.subSink.sink = form
      .get('valuationAdjustmentAmortizationStartDate')
      ?.valueChanges.subscribe(date => {
        this.legDateAttributeService.onValuationAdjustmentAmortizationStartDateChange(
          date
        );
      });
  }

  private valueAdjustAmrtEndDateListener(form: FormGroup): void {
    this.legHelperService.subSink.sink = form
      .get('valuationAdjustmentAmortizationEndDate')
      ?.valueChanges.subscribe(date => {
        this.legDateAttributeService.onValuationAdjustmentAmortizationEndDateChange(
          date
        );
      });
  }
}
